/**
 * Phase J.8 (2026-08-01) — "Released without invoice" dashboard widget.
 * Phase J.8c (2026-08-03) — self-hides when there is nothing actionable
 * (violations=0 AND edge_cases=0) or while the initial fetch is in
 * flight. TabGrid filters falsy children so no empty slot is left in
 * the layout.
 *
 * Data source: GET /api/admin/rentals/uninvoiced-releases (super_admin).
 * "See details" now deep-links to the rentals list pre-filtered to the
 * exact set surfaced here, via /admin/rentals?filter=uninvoiced_releases.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { AlertTriangle } from "lucide-react";

export default function UninvoicedReleasesCard() {
    const [loading, setLoading] = useState(true);
    const [counts, setCounts] = useState({ violations: 0, edge_cases: 0 });
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(
                    "/admin/rentals/uninvoiced-releases",
                    { params: { limit: 1 } },
                );
                if (!cancelled) setCounts(data?.counts || { violations: 0, edge_cases: 0 });
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const violations = Number(counts.violations || 0);
    const edge = Number(counts.edge_cases || 0);
    const zeroState = violations === 0 && edge === 0;

    // Phase J.8c — hide the card entirely while loading and when the
    // inbox is empty. TabGrid (see AdminDashboardPage) filters falsy
    // children so the grid closes the gap cleanly.
    if (loading || (!error && zeroState)) return null;

    if (error) {
        return (
            <div
                className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700"
                data-testid="uninvoiced-releases-card-error">
                Unable to load &quot;Released without invoice&quot; data.
            </div>
        );
    }

    return (
        <div
            className="rounded-md border p-4 space-y-2 border-red-200 bg-red-50"
            data-testid="uninvoiced-releases-card">
            <div className="flex items-center gap-2">
                <AlertTriangle size={16} className="text-red-700" />
                <h3 className="text-sm font-semibold text-red-900">
                    Released without invoice
                </h3>
            </div>
            <div className="space-y-1">
                <p
                    className="text-2xl font-display font-bold text-red-900 tabular-nums"
                    data-testid="uninvoiced-releases-count">
                    {violations}
                </p>
                <p className="text-xs text-red-900/80">
                    rental{violations === 1 ? "" : "s"} released without an invoice
                    {edge > 0 && (
                        <> · <span data-testid="uninvoiced-releases-edge-count">{edge}</span> edge case{edge === 1 ? "" : "s"}</>
                    )}
                </p>
                <Link
                    to="/admin/rentals?filter=uninvoiced_releases"
                    data-testid="uninvoiced-releases-details-link"
                    className="inline-block text-xs font-semibold text-red-800 underline hover:text-red-900">
                    See details →
                </Link>
            </div>
        </div>
    );
}
